import { updateObject } from 'shared/utility';
import * as actionTypes from 'store/actions/actionTypes';

const initialState = {
  sessionToken: null,
  UserId: null,
  sessionTokenError: null,
  signInError: null,
  signUpError: null,
  isAuthenticated: false,
  passwordResetError: null,
  passwordResetSuccess: null,
  ForceNewPassword: false,
  sessionTokenLoaded: false,
  accountVerified: false,
  CodeSentTo: null,
  verificationCodeResent: false,
  newUserCreated: false,
  error: null,
};

const sessionTokenSuccess = (state, action) => {
  return updateObject(state, {
    sessionToken: action.sessionToken,
    ForceNewPassword: false,
    sessionTokenLoaded: true,
  });
};

const sessionTokenFailed = (state, action) => {
  return updateObject(state, {
    sessionTokenError: action.error,
    ForceNewPassword: false,
  });
};

const signUpStart = (state) => {
  return updateObject(state, {
    signUpError: null,
    newUserCreated: false,
    error: null,
  });
};

const signUpSuccess = (state, action) => {
  localStorage.setItem('codeSentTo', action.CodeSentTo);
  return updateObject(state, {
    UserId: action.UserId,
    signUpError: null,
    newUserCreated: true,
    CodeSentTo: action.CodeSentTo,
    verificationCodeResent: false,
    error: null,
  });
};

const signUpFailed = (state, action) => {
  return updateObject(state, {
    signUpError: action.error,
  });
};

const verifyAccountStart = (state) => {
  return updateObject(state, {
    error: null,
    accountVerified: false,
    isAuthenticated: false,
  });
};

const verifyAccountSuccess = (state, action) => {
  localStorage.removeItem('codeSentTo', action.CodeSentTo);
  // Note, we only want to update the session token if a user just confirmed their email address, which occurs during
  // the signup process and is the first time that we receive a session token for the user. Otherwise, we do not want
  // to update the session token because switching a session token for the same user will force logout when the browser
  // is refreshed.
  return updateObject(state, {
    accountVerified: true,
    sessionToken: action.SessionToken && action.CodeSentTo === 'Email' ? action.SessionToken : state.sessionToken,
    CodeSentTo: action.VerifiedMedium,
    isAuthenticated: !!action.SessionToken,
  });
};

const verifyAccountFailed = (state, action) => {
  return updateObject(state, {
    error: action.error,
    verificationCodeResent: false,
  });
};

const resendAccountVerificationCodeStart = (state) => {
  return updateObject(state, {
    error: null,
    verificationCodeResent: false,
  });
};

const resendAccountVerificationCodeSuccess = (state, action) => {
  return updateObject(state, {
    CodeSentTo: action.CodeSentTo,
    verificationCodeResent: true,
  });
};

const resendAccountVerificationCodeFailed = (state, action) => {
  return updateObject(state, {
    error: action.error,
    verificationCodeResent: false,
  });
};

const accountNeedsVerification = (state, action) => {
  return updateObject(state, {
    CodeSentTo: action.CodeSentTo,
  });
};

const resetAuthData = (state) => {
  return updateObject(state, initialState);
};

const signInStart = (state) => {
  return updateObject(state, {
    signInError: null,
    ForceNewPassword: false,
    newUserCreated: false,
  });
};

const signInSuccess = (state, action) => {
  if (action.sessionToken) localStorage.removeItem('codeSentTo', action.CodeSentTo);
  return updateObject(state, {
    UserId: action.UserId,
    signInError: null,
    isAuthenticated: !!action.sessionToken,
    sessionToken: action.sessionToken,
    ForceNewPassword: action.ForceNewPassword,
    sessionTokenLoaded: true,
  });
};

const signInFailed = (state, action) => {
  let updatedState = null;
  // Here we handle the case when a attempts to sign-in when they have not yet verified their email/mobile number.
  // In this case, the api returns their userId and we need to take them to the '/verify' page for them to verify their email/mobile number.
  if (action.UserId) {
    updatedState = updateObject(state, {
      UserId: action.UserId,
      CodeSentTo: action.CodeSentTo ? action.CodeSentTo : null,
      verificationCodeResent: false,
      accountVerified: false,
      newUserCreated: true,
    });
  }
  // otherwise, we simply report the signInError
  else {
    updatedState = updateObject(state, {
      signInError: action.error,
    });
  }
  return updatedState;
};

const requestPasswordResetStart = (state) => {
  return updateObject(state, {
    passwordResetError: null,
    passwordResetSuccess: null,
  });
};

const requestPasswordResetSuccess = (state, action) => {
  return updateObject(state, {
    passwordResetSuccess: action.successMessage,
  });
};

const requestPasswordResetFailed = (state, action) => {
  return updateObject(state, {
    passwordResetError: action.error,
  });
};

const returnToPasswordResetRequest = (state) => {
  return updateObject(state, {
    passwordResetError: null,
    passwordResetSuccess: null,
  });
};

const logoutStart = (state) => {
  return updateObject(state, {
    isAuthenticated: false,
  });
};

const logoutSuccess = (state) => {
  return updateObject(state, {
    sessionToken: null,
    UserId: null,
    isAuthenticated: false,
    CodeSentTo: null,
    accountVerified: false,
    newUserCreated: false,
  });
};

const clearAuthErrors = (state) => {
  return updateObject(state, {
    passwordResetError: null,
    signInError: null,
    signUpError: null,
    error: null,
  });
};

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.SIGN_UP_START:
      return signUpStart(state, action);
    case actionTypes.SIGN_UP_SUCCESS:
      return signUpSuccess(state, action);
    case actionTypes.SIGN_UP_FAILED:
      return signUpFailed(state, action);
    case actionTypes.VERIFY_ACCOUNT_START:
      return verifyAccountStart(state, action);
    case actionTypes.VERIFY_ACCOUNT_SUCCESS:
      return verifyAccountSuccess(state, action);
    case actionTypes.VERIFY_ACCOUNT_FAILED:
      return verifyAccountFailed(state, action);
    case actionTypes.RESEND_ACCOUNT_VERIFICATION_CODE_START:
      return resendAccountVerificationCodeStart(state, action);
    case actionTypes.RESEND_ACCOUNT_VERIFICATION_CODE_SUCCESS:
      return resendAccountVerificationCodeSuccess(state, action);
    case actionTypes.RESEND_ACCOUNT_VERIFICATION_CODE_FAILED:
      return resendAccountVerificationCodeFailed(state, action);
    case actionTypes.ACCOUNT_NEEDS_VERIFICATION:
      return accountNeedsVerification(state, action);
    case actionTypes.SIGN_IN_START:
      return signInStart(state, action);
    case actionTypes.SIGN_IN_SUCCESS:
      return signInSuccess(state, action);
    case actionTypes.SIGN_IN_FAILED:
      return signInFailed(state, action);
    case actionTypes.LOGOUT_SUCCESS:
      return logoutSuccess(state, action);
    case actionTypes.LOGOUT_START:
      return logoutStart(state, action);
    case actionTypes.SESSION_TOKEN_SUCCESS:
      return sessionTokenSuccess(state, action);
    case actionTypes.SESSION_TOKEN_FAILED:
      return sessionTokenFailed(state, action);
    case actionTypes.REQUEST_PASSWORD_RESET_START:
      return requestPasswordResetStart(state, action);
    case actionTypes.REQUEST_PASSWORD_RESET_SUCCESS:
      return requestPasswordResetSuccess(state, action);
    case actionTypes.REQUEST_PASSWORD_RESET_FAILED:
      return requestPasswordResetFailed(state, action);
    case actionTypes.RETURN_TO_PASSWORD_RESET_REQUEST:
      return returnToPasswordResetRequest(state, action);
    case actionTypes.RESET_AUTH_DATA:
      return resetAuthData(state, action);
    case actionTypes.CLEAR_APP_ERROR:
      return clearAuthErrors(state, action);
    default:
      return state;
  }
};

export default reducer;
